import classNames from 'classnames';
import { StaticImage } from 'gatsby-plugin-image';

import CarrierFilter from '~components/CarrierLogos/CarrierLogoWrapper';
import Trustpilot from '~components/Trustpilot/Trustpilot';
import constants from '~globals/constants';
import BBBAccredited from '~images/inline/BBB.accredited.inline.svg';

type Props = {
    carrierlist: string;
    ctacopy?: string;
    backgroundsurface?: SupportedBackgroundColor;
    disablefilteredlinks?: boolean;
    includetrustpilot?: boolean;
    padding?: string;
};

const CarrierLogosRibbon = ({
    carrierlist,
    ctacopy = 'View all insurance carriers',
    backgroundsurface = 'bg-surface-100',
    disablefilteredlinks = false,
    includetrustpilot = false,
    padding,
}: Props) => {
    const getLogoWithTrackingIndex = (index: number): Record<string, JSX.Element> => ({
        aetna: (
            <CarrierFilter
                key={`aetna_${index}`}
                datawalink={`Carrier Ribbon:Carrier logo:${index}:aetna`}
                carriervalue={constants.CARRIER_ID.aetna}
                islink={!disablefilteredlinks}
                mobilesize="1/4"
                fullsize="48"
            >
                <StaticImage
                    src="../../images/logos/aetna.png"
                    alt="See Aetna health insurance plans"
                    width={120}
                />
            </CarrierFilter>
        ),
        anthem: (
            <CarrierFilter
                key={`anthem_${index}`}
                datawalink={`Carrier Ribbon:Carrier logo:${index}:anthem`}
                carriervalue={constants.CARRIER_ID.anthem}
                islink={!disablefilteredlinks}
                mobilesize="1/4"
                fullsize="44"
            >
                <StaticImage
                    src="../../images/logos/anthem.png"
                    alt="See Anthem health insurance plans"
                    width={176}
                />
            </CarrierFilter>
        ),
        cigna: (
            <CarrierFilter
                key={`cigna_${index}`}
                datawalink={`Carrier Ribbon:Carrier logo:${index}:cigna`}
                carriervalue={constants.CARRIER_ID.cigna}
                islink={!disablefilteredlinks}
                mobilesize="1/4"
                fullsize="44"
            >
                <StaticImage
                    src="../../images/logos/cigna.png"
                    alt="See Cigna health insurance plans"
                    width={122}
                />
            </CarrierFilter>
        ),
        humana: (
            <CarrierFilter
                key={`humana_${index}`}
                datawalink={`Carrier Ribbon:Carrier logo:${index}:humana`}
                carriervalue={constants.CARRIER_ID.humana}
                islink={!disablefilteredlinks}
                mobilesize="1/4"
                fullsize="40"
            >
                <StaticImage
                    src="../../images/logos/humana.png"
                    alt="See Humana health insurance plans"
                    width={160}
                />
            </CarrierFilter>
        ),
        kaiser: (
            <CarrierFilter
                key={`kaiser_${index}`}
                datawalink={`Carrier Ribbon:Carrier logo:${index}:kaiser`}
                carriervalue={constants.CARRIER_ID.kaiser}
                islink={!disablefilteredlinks}
                mobilesize="1/3"
                fullsize="48"
            >
                <StaticImage
                    src="../../images/logos/kaiser.png"
                    alt="See Kaiser health insurance plans"
                    width={192}
                />
            </CarrierFilter>
        ),
        mutualomaha: (
            // no filter because Mutual of Omaha only sells MS plans which are phone-only
            <CarrierFilter
                key={`mutualomaha_${index}`}
                islink={false}
                mobilesize="1/3"
                fullsize="52"
            >
                <StaticImage
                    src="../../images/logos/mutualomaha.png"
                    alt="See Mutual of Omaha health insurance plans"
                    width={208}
                />
            </CarrierFilter>
        ),
        united: (
            <CarrierFilter
                key={`united_${index}`}
                datawalink={`Carrier Ribbon:Carrier logo:${index}:united healthcare`}
                carriervalue={constants.CARRIER_ID.uhc}
                islink={!disablefilteredlinks}
                fullsize="48"
                mobilesize="1/3"
            >
                <StaticImage
                    src="../../images/logos/uhc.png"
                    alt="See United Healthcare health insurance plans"
                    width={192}
                />
            </CarrierFilter>
        ),
        wellcare: (
            <CarrierFilter
                key={`wellcare_${index}`}
                datawalink={`Carrier Ribbon:Carrier logo:${index}:wellcare`}
                carriervalue={constants.CARRIER_ID.wellcare}
                islink={!disablefilteredlinks}
                mobilesize="1/4"
                fullsize="40"
            >
                <StaticImage
                    src="../../images/logos/wellcare.png"
                    alt="See Wellcare health insurance plans"
                    width={160}
                />
            </CarrierFilter>
        ),
    });

    return (
        <>
            <section className={classNames('w-full px-4 mx-auto', padding, backgroundsurface)}>
                <div
                    className={classNames(
                        'flex flex-row flex-wrap justify-center',
                        'gap-x-6 gap-y-3'
                    )}
                >
                    {carrierlist.split(',').map((carrier, i) => {
                        return getLogoWithTrackingIndex(i + 1)[carrier];
                    })}
                    <div className="flex flex-col justify-center items-center self-stretch flex-shrink-0 w-60">
                        <a
                            href={`${process.env.GATSBY_EHI_URL}about-ehealth/carrier-partners/`}
                            className="font-poppins text-primary text-16 leading-24 lg:text-18 lg:leading-28 text-center underline whitespace-nowrap"
                            target="_blank"
                            rel="noreferrer"
                            data-qa="view-all-carriers"
                        >
                            {ctacopy}
                        </a>
                    </div>
                </div>
            </section>
            {includetrustpilot && (
                <>
                    <div className="px-4 md:px-6 lg:px-0">
                        <hr className="text-inactive md:max-w-screen-lg lg:max-w-screen-xl mx-auto" />
                    </div>
                    <div className="py-8 flex flex-col md:flex-row items-center justify-center">
                        <Trustpilot size="newthin" className="max-w-[480px] flex-1" />
                        <div className="pt-8 md:pt-0 flex justify-center min-h-[72px] md:min-h-10">
                            <div className="pr-2">
                                <div
                                    className="norton-placeholder w-20"
                                    id="DigiCertClickID_onwANETs_16"
                                />
                            </div>
                            <div className="pl-2">
                                <a
                                    href="https://www.bbb.org/us/ca/santa-clara/profile/insurance-companies/ehealthinsurance-services-1216-359700#bbbseal"
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="BBB Accredited Business, BBB rating A plus"
                                >
                                    <BBBAccredited width={85} height={30} alt="" loading="lazy" />
                                </a>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default CarrierLogosRibbon;
